// client/src/App.tsx
import { Switch, Route, useLocation } from 'wouter';
import { queryClient } from './lib/queryClient';
import { QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense, Component, useEffect, useRef, useState } from 'react';
import TabNavigation from './components/TabNavigation';
import BottomNavigation from './components/BottomNavigation';
import { BottomToaster } from './components/ui/bottom-toaster';
import { AppProvider, useAppContext, TabType } from './lib/AppContext';
import AuthPromptSheet from './components/AuthPromptSheet';
import { AuthProvider, useAuthContext } from './auth-context';
import { ProtectedRoute } from './lib/protected-route';
import ResetWithCodePage from './pages/ResetWithCode';
import ResetWithOTPPage from './pages/ResetWithOTP';
import Profile from './pages/Profile';
import { useQuery } from '@tanstack/react-query';
import { supabase } from './lib/supabase';
import MessageThread from './pages/MessageThread';
import PropertyDetail from './pages/Property';
import FiltersPage from './pages/FiltersPage';
import Map from './pages/Map';
import { HomeFiltersProvider } from './pages/Home/context';
import { useToast } from './components/ui/use-toast';
import { clearAllCaches, restoreCacheFromLocalStorage } from './lib/utils/centralized-cache';


// Preload critical chunks
const preloadChunk = (importFn: () => Promise<any>) => {
  const link = document.createElement('link');
  link.rel = 'modulepreload';
  link.href = importFn.toString().match(/['"](.*?)['"]/)?.[1] || '';
  document.head.appendChild(link);
};

// Dynamically import page components with preloading
const NotFound = lazy(() => import('./pages/not-found'));
const Home = lazy(() => import('./pages/Home'));
const Transactions = lazy(() => import('./pages/Transactions'));
const Schedule = lazy(() => import('./pages/Schedule'));
const Messages = lazy(() => import('./pages/Messages'));
const ArchivedMessages = lazy(() => import('./pages/ArchivedMessages'));
const NewAppointment = lazy(() => import('./pages/NewAppointment'));
const AuthPage = lazy(() => import('./pages/auth-page'));
const ChangePassword = lazy(() => import('./pages/ChangePassword'));
const ChangeEmail = lazy(() => import('./pages/ChangeEmail'));
const Friends = lazy(() => import('./pages/Friends'));
const MarketStats = lazy(() => import('./pages/MarketStats'));
const Favorites = lazy(() => import('./pages/Favorites'));
const AppointmentSuccess = lazy(() => import('./pages/AppointmentSuccess'));
const EmailChangeSuccess = lazy(() => import('./pages/EmailChangeSuccess'));

// Loading component with skeleton
const LoadingSkeleton = () => (
  <div className="animate-pulse">
    <div className="h-8 bg-gray-200 rounded w-3/4 mb-4"></div>
    <div className="space-y-3">
      <div className="h-4 bg-gray-200 rounded"></div>
      <div className="h-4 bg-gray-200 rounded w-5/6"></div>
      <div className="h-4 bg-gray-200 rounded w-4/6"></div>
    </div>
  </div>
);

// Error Boundary Component
class ErrorBoundary extends Component<{ children: React.ReactNode }, { hasError: boolean; error: Error | null }> {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): { hasError: boolean; error: Error } {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError && this.state.error) {
      const error = this.state.error as Error;
      return (
        <div className="flex justify-center items-center h-screen text-red-600">
          <div>
            <h1>Error: {error.message}</h1>
            <pre>{error.stack || 'No stack trace available'}</pre>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function AppContent() {
  const [location, setLocation] = useLocation();
  const { setActiveTab, isAuthenticated } = useAppContext();
  const { user, loading, error } = useAuthContext();
  const { toast } = useToast();



  // Preload next likely chunks based on current location
  useEffect(() => {
    const currentPath = location.split('/')[1] || 'home';

    // Preload chunks for adjacent routes
    switch (currentPath) {
      case 'home':
        preloadChunk(() => import('./pages/Schedule'));
        preloadChunk(() => import('./pages/Messages'));
        break;
      case 'schedule':
        preloadChunk(() => import('./pages/Home'));
        preloadChunk(() => import('./pages/NewAppointment'));
        break;
      case 'messages':
        preloadChunk(() => import('./pages/Home'));
        preloadChunk(() => import('./pages/Profile'));
        break;
      case 'profile':
        preloadChunk(() => import('./pages/Messages'));
        preloadChunk(() => import('./pages/Friends'));
        break;
    }
  }, [location]);

  // Handle auth state changes and navigation
  useEffect(() => {
    console.log('🔄 [App] ===== AUTH REDIRECT CHECK =====');
    console.log('🔄 [App] Current location:', location);
    console.log('🔄 [App] Loading state:', loading);
    console.log('🔄 [App] Is authenticated:', isAuthenticated);
    console.log('🔄 [App] User:', user ? `${user.email} (${user.id})` : 'null');
    
    if (loading) {
      console.log('🔄 [App] ⏳ Still loading, skipping redirect check');
      return;
    }

    const isOnAuth = location.startsWith('/auth');
    const isOnResetOtp = location.startsWith('/reset-otp');
    const isOnEmailSuccess = location === '/email-change-success';
    
    console.log('🔄 [App] Route checks:', {
      isOnAuth,
      isOnResetOtp,
      isOnEmailSuccess,
      isOnMap: location === '/map'
    });
    
    // Respect a short grace period while deeplink processing is occurring
    const deeplinkProcessing = (() => { 
      try { 
        const flag = sessionStorage.getItem('deeplink_processing') === '1';
        console.log('🔄 [App] Deeplink processing flag:', flag);
        return flag;
      } catch { 
        console.log('🔄 [App] Failed to check deeplink flag');
        return false; 
      } 
    })();
    
    if (deeplinkProcessing) {
      return;
    }

    // Special handling for email change success page - allow access regardless of auth state
    if (isOnEmailSuccess) {
      console.log('🔄 [App] 📧 On email change success page - allowing access');
      console.log('🔄 [App] 📧 Auth state for email success page - isAuthenticated:', isAuthenticated);
      console.log('🔄 [App] 📧 User for email success page:', user ? user.email : 'null');
      return;
    }

    // Only redirect to /auth if not on /auth, /reset-otp, or /map
    if (!isAuthenticated && !isOnAuth && !isOnResetOtp && location !== '/map') {
      console.log('🔄 [App] 🔒 Not authenticated, redirecting to /auth');
      setLocation('/auth', { replace: true });
      return;
    }

    if (isAuthenticated && isOnAuth) {
      console.log('🔄 [App] ✅ Authenticated on auth page, redirecting to home');
      setLocation('/', { replace: true });
      return;
    }
    
    console.log('🔄 [App] ✅ No redirect needed');
    console.log('🔄 [App] ===== END AUTH REDIRECT CHECK =====');
  }, [isAuthenticated, user, loading, location, setLocation]);

  // Update active tab based on current location
  useEffect(() => {
    const path = location.split('/')[1] || 'home';
    setActiveTab(path as TabType);
  }, [location, setActiveTab]);

  // Reset scroll position on location change
  useEffect(() => {
    // Reset scroll to top when navigating to a new page
    const scrollContainer = document.querySelector('div.flex-1.overflow-y-auto') as HTMLElement | null;
    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
    // Also reset window scroll as fallback
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location]);

  // Monitor Supabase auth state changes
  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      // Handle auth state changes silently
    });
    return () => {
      sub.subscription.unsubscribe();
    };
  }, []);

  const isOnAuthScreens = location.startsWith('/auth') || location.startsWith('/reset-otp');
  const isOnMap = location === '/map';
  const isOnEmailSuccess = location === '/email-change-success';
  const showNavigation = isAuthenticated && !isOnAuthScreens && !isOnEmailSuccess;
  const showBottomNavigation = showNavigation && !isOnMap && !isOnEmailSuccess;

  if (loading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }

  return (
    <div className="max-w-md mx-auto h-screen flex flex-col bg-white overflow-hidden relative">
      {showNavigation && <TabNavigation />}
      <div className={`flex-1 ${isOnMap || isOnAuthScreens ? 'overflow-hidden' : 'overflow-y-auto'} ${showBottomNavigation ? 'pb-20' : ''}`}>
        <Suspense
          fallback={
            <div className="p-4">
              <LoadingSkeleton />
            </div>
          }
        >
          <Switch>
            <Route path="/auth" component={AuthPage} />
            <Route path="/reset-with-code" component={ResetWithCodePage} />
            <Route path="/reset-otp" component={ResetWithOTPPage} />
            <ProtectedRoute path="/" component={Home} user={user} loading={loading} />
            <ProtectedRoute
              path="/transactions"
              component={Transactions}
              user={user}
              loading={loading}
            />
            <ProtectedRoute
              path="/schedule"
              component={Schedule}
              user={user}
              loading={loading}
            />
            <ProtectedRoute
              path="/schedule/new"
              component={NewAppointment}
              user={user}
              loading={loading}
            />
            <ProtectedRoute
              path="/schedule/success"
              component={AppointmentSuccess}
              user={user}
              loading={loading}
            />
            <ProtectedRoute
              path="/appointments/:id/edit"
              component={NewAppointment}
              user={user}
              loading={loading}
            />
            <ProtectedRoute
              path="/messages"
              component={Messages}
              user={user}
              loading={loading}
            />
            <ProtectedRoute
              path="/messages/archived"
              component={ArchivedMessages}
              user={user}
              loading={loading}
            />
            <ProtectedRoute
              path="/favorites"
              component={Favorites}
              user={user}
              loading={loading}
            />
            <Route
              path="/messages/:userId"
              component={({ params }) => {
                // Preserve query string (e.g., from=profile, user=)
                return <MessageThread userId={params.userId} />;
              }}
            />
            <ProtectedRoute
              path="/profile"
              component={Profile}
              user={user}
              loading={loading}
            />
            <Route
              path="/profile/:userId"
              component={({ params }) => {
                const { userId } = params;
                const { data: publicUser, isLoading } = useQuery({
                  queryKey: ['public-profile', userId],
                  queryFn: async () => {
                    const { data, error } = await supabase
                      .from('users')
                      .select('id, name, email, avatar, bio, city, role')
                      .eq('id', userId)
                      .single();
                    if (error) throw error;
                    return data;
                  },
                });
                if (isLoading) return <div>Loading...</div>;
                if (!publicUser) return <div>User not found.</div>;
                return <Profile publicUser={publicUser} />;
              }}
            />
            <ProtectedRoute
              path="/change-password"
              component={ChangePassword}
              user={user}
              loading={loading}
            />
            <ProtectedRoute
              path="/change-email"
              component={ChangeEmail}
              user={user}
              loading={loading}
            />
            <ProtectedRoute
              path="/email-change-success"
              component={EmailChangeSuccess}
              user={user}
              loading={loading}
            />
            <ProtectedRoute
              path="/friends"
              component={Friends}
              user={user}
              loading={loading}
            />
            <ProtectedRoute
              path="/search"
              component={Friends}
              user={user}
              loading={loading}
            />
            <ProtectedRoute
              path="/market-stats"
              component={MarketStats}
              user={user}
              loading={loading}
            />
            <Route path="/property/:id" component={PropertyDetail} />
            <Route path="/filters" component={FiltersPage} />
            <Route path="/map" component={Map} />
            <Route component={NotFound} />
          </Switch>
        </Suspense>
      </div>
      {showBottomNavigation && <BottomNavigation />}
      <BottomToaster />
      
    </div>
  );
}

function AppWithProviders() {
  const renderCount = useRef(0);
  renderCount.current += 1;

  // Clear stale cache on app startup to prevent loading issues
  useEffect(() => {
    // Check if we have search results in localStorage before clearing caches
    const hasSearchResults = () => {
      try {
        const listings = localStorage.getItem('homeFilters_listings');
        return listings && JSON.parse(listings).length > 0;
      } catch {
        return false;
      }
    };

    // Only clear caches if we don't have existing search results
    // This prevents the property details page from losing access to cached search results
    if (!hasSearchResults()) {
      clearAllCaches();
      queryClient.clear();
    } else {
      
      // Clear localStorage cache to force fresh data from database
      localStorage.removeItem('homeFilters_listings');
    }
  }, []);

  const [activeTab, setActiveTab] = useState<TabType>('home');
  const { user, loading, error, register, login, logout, resetPassword, clearError, setUser } = useAuthContext();

  const [isGuest, setIsGuest] = useState<boolean>(() => {
    try {
      return localStorage.getItem('guest_session') === '1';
    } catch {
      return false;
    }
  });

  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [authPromptMessage, setAuthPromptMessage] = useState('Create an account or sign in to save favorites.');

  const startGuestSession = () => {
    setIsGuest(true);
    try { localStorage.setItem('guest_session', '1'); } catch {}
  };

  const endGuestSession = () => {
    setIsGuest(false);
    try { localStorage.removeItem('guest_session'); } catch {}
  };

  const openAuthPrompt = (message?: string) => {
    if (message) setAuthPromptMessage(message);
    setShowAuthPrompt(true);
  };

  const closeAuthPrompt = () => {
    setShowAuthPrompt(false);
    setAuthPromptMessage('Create an account or sign in to save favorites.');
  };

  // Register FCM token function
  const registerFCMToken = async (token: string) => {
    if (!user) return;
    try {
      const response = await fetch('/api/users/fcm-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.accessToken}`,
        },
        body: JSON.stringify({ token }),
      });
      if (!response.ok) {
        throw new Error('Failed to register FCM token');
      }
    } catch (error) {
      // Silent error handling
    }
  };

  return (
    <AppProvider
      value={{
        auth: {
          user,
          loading,
          error,
          register,
          login,
          logout,
          resetPassword,
          clearError,
          setUser,
        },
        activeTab,
        setActiveTab,
        isAuthenticated: !!user || isGuest,
        isGuest,
        startGuestSession,
        endGuestSession,
        openAuthPrompt,
        closeAuthPrompt,
        showAuthPrompt,
        authPromptMessage,
        registerFCMToken,
        currentUser: user,
      }}
    >
      <AppContent />
      <AuthPromptSheet />
    </AppProvider>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <AuthProvider>
          <AppWithProviders />
        </AuthProvider>
      </ErrorBoundary>
    </QueryClientProvider>
  );
}

export default App;